// src/components/IssueSelection.js
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Select,
  MenuItem,
  CircularProgress,
  Tooltip,
  Button
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useLocation, useNavigate } from 'react-router-dom';

const IssueSelection = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { projectId, severityLevel, runId, reportId } = location.state || {};
  const [components, setComponents] = useState([]);
  const [issues, setIssues] = useState([]);
  const [totalIssues, setTotalIssues] = useState(0);
  // const [total, setTotal] = useState(0);
  const token = localStorage.getItem('access_token')

  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [severityFilter, setSeverityFilter] = useState(severityLevel || 'ALL');
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const REPORT_API_HOST = process.env.REACT_APP_REPORT_API_NEW_HOST;
  // const SONARQUBE_HOST = process.env.REACT_APP_SONARQUBE_HOST;
  // const SONARQUBE_TOKEN = process.env.REACT_APP_SONARQUBE_TOKEN;

  const fetchIssues = useCallback(async () => {
    setLoading(true);
    try {
      const severityParam = severityFilter !== 'ALL' ? `&severity=${severityFilter}` : '';
      // const severityParam = severityFilter !== 'ALL' ? `&severities=${severityFilter}` : '';
      const response = await fetch(
        `${REPORT_API_HOST}/athrv/api/v1/platform/report/sonarqube/issues?project_id=${projectId}${severityParam}&p=${page + 1}&ps=${rowsPerPage}`,
        // `${SONARQUBE_HOST}/api/issues/search?componentKeys=${projectId}${severityParam}&p=${page + 1}&ps=${rowsPerPage}`,
        {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`
            // Authorization: `Basic ${btoa(`${SONARQUBE_TOKEN}:`)}`, // Basic Auth for sonar
          },
        }
      );
      const data = await response.json();
      setComponents(data.components || []);
      setIssues(data.issues || []);
      setTotalIssues(data.total);
      // setTotal(data.paging.total || 0);
    } catch (error) {
      console.error('Error fetching issues:', error);
    } finally {
      setLoading(false);
    }
  }, [projectId, page, rowsPerPage, severityFilter]);


  useEffect(() => {
    fetchIssues();
  }, [fetchIssues]);

  const handleChangePage = (event, newPage) => setPage(newPage);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSeverityChange = (event) => {
    setSeverityFilter(event.target.value);
    setPage(0);
  };

  const handleRowClick = (issue) => {
    navigate(`/issues/${projectId}/${issue.key}`, {
      state: {
        issueKey: issue.key,
        message: issue.message,
        textRange: issue.textRange,
      },
    });
  };

  return (
    <Box sx={{ p: 2 }}>
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', marginBottom: '20px' }}>
            <Typography variant="h5" sx={{ marginBottom: '10px' }}>Issue Selection</Typography>
            <Box sx={{ display: 'flex', gap: '20px', flexWrap: 'wrap', marginBottom: '10px' }}>
              {projectId && (
                <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                  Project ID: <span style={{ fontWeight: 'normal' }}>{projectId}</span>
                </Typography>
              )}
              {runId && (
                <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                  Run ID: <span style={{ fontWeight: 'normal' }}>{runId}</span>
                </Typography>
              )}
              {reportId && (
                <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                  Report ID: <span style={{ fontWeight: 'normal' }}>{reportId}</span>
                </Typography>
              )}
            </Box>
            <Select
              value={severityFilter}
              onChange={handleSeverityChange}
              displayEmpty
              sx={{ width: 200 }}
            >
              <MenuItem value="ALL">All Severities</MenuItem>
              <MenuItem value="BLOCKER">Blocker</MenuItem>
              <MenuItem value="CRITICAL">Critical</MenuItem>
              <MenuItem value="MAJOR">Major</MenuItem>
              <MenuItem value="MINOR">Minor</MenuItem>
              <MenuItem value="INFO">Info</MenuItem>
            </Select>
          </Box>

          <Button
            variant="contained"
            className='athrv-dash-primary-button'
            onClick={() => {
              console.log("Navigating with activeTab = 1");
              navigate(`/projects/${projectId}`, { state: { activeTab: 2 } });
            }}
            sx={{ marginTop: 3 }}
          >
            Back to Project Details
          </Button>
        </Box>


        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  {/* <TableCell>Key</TableCell> */}
                  <TableCell>Severity</TableCell>
                  <TableCell>Message</TableCell>
                  <TableCell>Component</TableCell>
                  <TableCell>Line</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {issues.map((issue) => (
                  <TableRow
                    key={issue.key}
                    hover
                    onClick={() => handleRowClick(issue)}
                    sx={{ cursor: 'pointer' }}
                  >
                    {/* <TableCell>{issue.key}</TableCell> */}
                    <TableCell>{issue.severity}</TableCell>
                    <TableCell>
                      <Tooltip title={issue.message} placement="top" arrow>
                        <Typography noWrap>{issue.message}</Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell>{issue.component}</TableCell>
                    <TableCell>{issue.line}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <TablePagination
              rowsPerPageOptions={[10, 20, 50, 100]}
              component="div"
              count={totalIssues}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </TableContainer>
        )}
      </Paper>

    </Box>
  );
};

export default IssueSelection;